import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';
import { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';

import { trackActivitiesFilter } from '@libs/google-analytics-events';
import type {
  DestinationProduct,
  HotelIngredientResponse,
  ContentApiImage,
  ActivityProductType,
} from '@libs/trip-planner-interfaces';
import { AvailabilityOrder } from '@libs/trip-planner-interfaces';
import { toggleValue } from '@libs/utils';

import type {
  ActivitySelectionCardData,
  ActivitySelectionDestinationData,
  ActivityType,
} from '../../models';
import { ActivitySelectionViewType } from '../../models';

export function getActivitySelectionHeaderData(
  viewType: ActivitySelectionViewType,
  stayIngredient: HotelIngredient | OwnStayIngredient | null,
  hotelIngredientResponse: HotelIngredientResponse | undefined,
  activeFiltersAmount: number
): ActivitySelectionDestinationData | undefined {
  if (!stayIngredient) {
    return undefined;
  }

  return {
    destinationText: stayIngredient.displayText,
    numberOfNights:
      stayIngredient.numberOfNights ?? hotelIngredientResponse?.numberOfNights,
    filtersPanelActive: viewType === ActivitySelectionViewType.Filters,
    activeFiltersAmount,
  };
}

export function filterActivities(
  activityList: DestinationProduct[]
): DestinationProduct[] {
  const hasNoImage = (product: DestinationProduct): boolean =>
    product.images.length !== 0;
  const hasDescription = (product: DestinationProduct): boolean =>
    product.openingParagraph?.value !== undefined;

  return activityList.filter(
    (product) =>
      product.totalPrice && hasNoImage(product) && hasDescription(product)
  );
}

export function sortActivities(
  activityList: DestinationProduct[],
  sortBy: AvailabilityOrder
): DestinationProduct[] {
  if (sortBy === AvailabilityOrder.Cheapest) {
    return activityList.sort((activityA, activityB) => {
      if (activityA.totalPrice && activityB.totalPrice) {
        return activityA.totalPrice.amount - activityB.totalPrice.amount;
      }
      return 0;
    });
  } else {
    return activityList.sort(
      (activityA, activityB) =>
        activityB.popularityScore - activityA.popularityScore
    );
  }
}

export function mapDestinationProductToActivitySelectionCard(
  product: DestinationProduct,
  formatMoney: (amount: number, currency: string) => string,
  getImageUrl: (
    image?: ContentApiImage,
    hotelCode?: string,
    width?: number,
    height?: number
  ) => string,
  selectedActivityCodes: string[]
): ActivitySelectionCardData {
  const subtitle = getActivitySelectionCardSubtitle(product);
  const content = openingParagraphToBulletPoints(
    product.openingParagraph?.value ?? ''
  );
  return {
    activitySelectionCardContent: {
      title: product.name,
      subtitle,
      stars: product.semboSunRating,
      content,
      price: formatMoney(
        product.totalPrice?.amount || 0,
        product.totalPrice?.currency || ''
      ),
      freeCancellation: false,
    },
    imageUrls: product.images.map((image) => getImageUrl(image)),
    activityCode: product.hotelCode,
    activityType: product.typeOfHotel as ActivityType,
    isSelected: selectedActivityCodes.includes(product.hotelCode),
  };
}

export function getActivitySelectionCardSubtitle(
  product: DestinationProduct
): string {
  if (product.mainPolygon) {
    return product.mainPolygon.fullName.toLocaleLowerCase();
  }

  if (product.address?.city) {
    return product.address.country
      ? `${product.address.city}, ${product.address.country}`.toLocaleLowerCase()
      : product.address.city.toLocaleLowerCase();
  }

  return '';
}

export function openingParagraphToBulletPoints(value: string): string[] {
  const bulletPointAmount = 3;

  return value
    .split('\n')
    .map((item: string) => item.replaceAll('**', ''))
    .map((item: string) => item.replaceAll('\r', ''))
    .map((item: string) => item.replace(/<\/?[^>]+(>|$)/g, ''))
    .map((item: string) => {
      const text = item.match(/[a-zA-Z].*/);
      return text ? text[0] : item;
    })
    .filter((item: string) => item !== '')
    .slice(0, bulletPointAmount);
}

export function keepWrapperScrollPositionOnTop(): void {
  const activitySelectionContentLeftColumnBottomWrapper =
    document.getElementById('activitySelectionContentLeftColumnBottomWrapper');
  activitySelectionContentLeftColumnBottomWrapper?.scrollTo({
    top: 0,
  });
}

export function toggleType(
  hotelType: ActivityProductType,
  currentStay: HotelIngredient | OwnStayIngredient
): void {
  trackActivitiesFilter();
  toggleValue(hotelType, currentStay.filters.selectedActivityTypes);
}

export function setFilterText(
  filterText: string,
  currentStay: HotelIngredient | OwnStayIngredient
): void {
  trackActivitiesFilter();
  currentStay.filters.activitiesFilterText = filterText;
}

export function sortDestinationProducts(
  destinationProducts: DestinationProduct[],
  suggestionType: SuggestionType,
  selectedActivityCode?: string
): DestinationProduct[] {
  let availableProducts = filterActivities(destinationProducts);

  const availabilityOrder =
    suggestionType === SuggestionType.MostPopular
      ? AvailabilityOrder.Popular
      : AvailabilityOrder.Cheapest;

  availableProducts = sortActivities(availableProducts, availabilityOrder);

  if (availableProducts.length > 0) {
    if (selectedActivityCode) {
      availableProducts = moveSelectedActivityToFirstPostion(
        availableProducts,
        selectedActivityCode
      );
    }
  }

  return availableProducts;
}

function moveSelectedActivityToFirstPostion(
  initialActivityList: DestinationProduct[],
  activityCode: string | undefined
): DestinationProduct[] {
  const activityList = [...initialActivityList];

  if (activityCode) {
    const activityIndex = activityList.findIndex(
      (product) => product.hotelCode === activityCode
    );
    if (activityIndex > 0) {
      const selectedActivity = activityList.splice(activityIndex, 1);
      activityList.unshift(...selectedActivity);
    }
  }
  return activityList;
}
